/**
 * Input Validation Utilities for Solar Financial Model
 * Centralized validation logic for all model parameters
 */

/**
 * Input bounds for validation
 */
export const INPUT_BOUNDS = {
    installedCapacity: [0.1, 10000],
    annualHours: [0, 8760],
    onGridTariff: [0.001, 1.0],
    operationPeriod: [1, 50],
    constructionPeriod: [1, 10],
    investmentPerMW: [1000, 10000000],
    longTermLoanRate: [0, 0.5],
    discountRate: [0.01, 0.5],
    pvDegradationRate: [0, 0.1],
    operatingCostRate: [0, 1],
    operatingCostEscalationRate: [0, 0.5],
    ownFunds: [1, 1000000000],
    depreciationPeriod: [1, 50],
    salvageValueRate: [0, 1],
    vatRate: [0, 1],
    surtaxRate: [0, 1],
    incomeTaxRate: [0, 1],
    statutoryReserveRate: [0, 1],
    discretionaryReserveRate: [0, 1],
    profitDistributionRate: [0, 1],
    goalSeekTargetIRR: [0.01, 0.5],
    landCostPerHectare: [0, 247000],
    hectaresPerMW: [0.4, 8.1],
    
    // BESS Parameters
    bessEnergyCapacity: [0, 10000],
    bessPowerCapacity: [0, 10000],
    bessCostPerMWh: [10000, 1000000],
    bessPowerCostPerMW: [10000, 1000000],
    bessRoundTripEfficiency: [0.5, 1],
    bessDegradationRate: [0, 0.1],
    bessOperatingCostRate: [0, 100000],
    bessReplacementPeriod: [5, 30],
    bessReplacementCostFactor: [0.1, 2],
    bessArbitrageCycles: [0, 5],
    bessArbitrageSpread: [0, 2],
    bessAncillaryRate: [0, 100000],
    bessCapacityRate: [0, 50000],
    bessDemandChargeReduction: [0, 50000]
};

/**
 * Validates a single input value
 */
export const validateInputValue = (id, value) => {
    const numValue = Number(value);
    
    // Check if it's a valid number
    if (isNaN(numValue) || !isFinite(numValue)) {
        return {
            valid: false,
            message: `${id} must be a valid number`
        };
    }
    
    // Check bounds if they exist
    if (INPUT_BOUNDS[id]) {
        const [min, max] = INPUT_BOUNDS[id];
        if (numValue < min || numValue > max) {
            return {
                valid: false,
                message: `${id} must be between ${min} and ${max}`
            };
        }
    }
    
    return { valid: true, value: numValue };
};

/**
 * Validates all input parameters
 */
export const validateAllInputs = (inputs) => {
    const errors = [];
    const warnings = [];
    const validatedInputs = {};
    
    Object.entries(inputs).forEach(([key, value]) => {
        const validation = validateInputValue(key, value);
        
        if (validation.valid) {
            validatedInputs[key] = validation.value;
        } else {
            errors.push(`${key}: ${validation.message}`);
        }
    });
    
    // Check for required fields
    const requiredFields = ['installedCapacity', 'investmentPerMW', 'ownFunds', 'operationPeriod'];
    requiredFields.forEach(field => {
        if (!validatedInputs[field] || validatedInputs[field] <= 0) {
            errors.push(`${field} is required and must be greater than 0`);
        }
    });
    
    // Business logic validations
    if (validatedInputs.ownFunds && validatedInputs.installedCapacity && validatedInputs.investmentPerMW) {
        const totalInvestment = validatedInputs.installedCapacity * validatedInputs.investmentPerMW;
        if (validatedInputs.ownFunds > totalInvestment) {
            warnings.push('Own funds exceed total investment - this is unusual but allowed');
        }
    }
    
    if (validatedInputs.constructionPeriod && validatedInputs.operationPeriod) {
        if (validatedInputs.constructionPeriod >= validatedInputs.operationPeriod) {
            warnings.push('Construction period should typically be shorter than operation period');
        }
    }
    
    if (validatedInputs.discountRate && validatedInputs.longTermLoanRate) {
        if (validatedInputs.discountRate < validatedInputs.longTermLoanRate) {
            warnings.push('Discount rate is lower than loan rate - check WACC calculation');
        }
    }
    
    return {
        valid: errors.length === 0,
        errors,
        warnings,
        validatedInputs
    };
};

/**
 * Validates BESS-specific parameters
 */
export const validateBessParameters = (inputs) => {
    if (!inputs.bessEnabled) {
        return { valid: true, errors: [], warnings: [] };
    }
    
    const errors = [];
    const warnings = [];
    
    // Required BESS fields
    const bessRequired = ['bessEnergyCapacity', 'bessPowerCapacity', 'bessCostPerMWh', 'bessPowerCostPerMW'];
    bessRequired.forEach(field => {
        if (!inputs[field] || inputs[field] <= 0) {
            errors.push(`${field} is required when BESS is enabled`);
        }
    });
    
    // BESS business logic validations
    if (inputs.bessEnergyCapacity && inputs.bessPowerCapacity) {
        const duration = inputs.bessEnergyCapacity / inputs.bessPowerCapacity;
        if (duration < 0.5) {
            warnings.push('BESS duration is very short (< 0.5 hours) - consider increasing energy capacity');
        }
        if (duration > 8) {
            warnings.push('BESS duration is very long (> 8 hours) - this may not be economical');
        }
    }
    
    if (inputs.bessRoundTripEfficiency && inputs.bessRoundTripEfficiency < 0.8) {
        warnings.push('BESS round-trip efficiency is quite low - consider newer technology');
    }
    
    if (inputs.bessDegradationRate && inputs.bessDegradationRate > 0.025) {
        warnings.push('BESS degradation rate is high (> 2.5%/year) - check battery technology');
    }
    
    if (inputs.bessArbitrageCycles && inputs.bessArbitrageCycles > 2) {
        warnings.push('More than 2 arbitrage cycles per day may accelerate battery degradation');
    }
    
    return {
        valid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Sanitizes input value for safe processing
 */
export const sanitizeInputValue = (value) => {
    if (typeof value === 'string') {
        // Remove any non-numeric characters except decimal point and minus sign
        value = value.replace(/[^0-9.-]/g, '');
    }
    
    const numValue = Number(value);
    
    // Return 0 for invalid numbers instead of NaN
    return (isNaN(numValue) || !isFinite(numValue)) ? 0 : numValue;
};

/**
 * Formats validation messages for display
 */
export const formatValidationMessage = (errors, warnings) => {
    let message = '';
    
    if (errors.length > 0) {
        message += 'Errors:\n' + errors.map(e => '• ' + e).join('\n');
    }
    
    if (warnings.length > 0) {
        if (message) message += '\n\n';
        message += 'Warnings:\n' + warnings.map(w => '• ' + w).join('\n');
    }
    
    return message;
};

/**
 * Gets user-friendly field names for display
 */
export const getFieldDisplayName = (fieldId) => {
    const displayNames = {
        installedCapacity: 'Installed Capacity',
        investmentPerMW: 'Investment per MW',
        annualHours: 'Annual Hours',
        onGridTariff: 'On-Grid Tariff',
        pvDegradationRate: 'PV Degradation Rate',
        constructionPeriod: 'Construction Period',
        operationPeriod: 'Operation Period',
        landCostPerHectare: 'Land Cost per Hectare',
        hectaresPerMW: 'Hectares per MW',
        ownFunds: 'Own Funds',
        longTermLoanRate: 'Loan Interest Rate',
        discountRate: 'Discount Rate (WACC)',
        operatingCostRate: 'Operating Cost Rate',
        operatingCostEscalationRate: 'Operating Cost Escalation',
        depreciationPeriod: 'Depreciation Period',
        salvageValueRate: 'Salvage Value Rate',
        goalSeekTargetIRR: 'Target Project IRR',
        
        // BESS fields
        bessEnergyCapacity: 'BESS Energy Capacity',
        bessPowerCapacity: 'BESS Power Capacity',
        bessCostPerMWh: 'BESS Energy Cost',
        bessPowerCostPerMW: 'BESS Power Cost',
        bessRoundTripEfficiency: 'BESS Round-trip Efficiency',
        bessDegradationRate: 'BESS Degradation Rate',
        bessOperatingCostRate: 'BESS Operating Cost',
        bessReplacementPeriod: 'BESS Replacement Period',
        bessReplacementCostFactor: 'BESS Replacement Cost Factor',
        bessArbitrageCycles: 'Arbitrage Cycles per Day',
        bessArbitrageSpread: 'Arbitrage Spread',
        bessAncillaryRate: 'Ancillary Services Rate',
        bessCapacityRate: 'Capacity Revenue Rate',
        bessDemandChargeReduction: 'Demand Charge Reduction'
    };
    
    return displayNames[fieldId] || fieldId;
}; 